import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

interface BillingKPI {
    yearTotal: number;
    monthTotal: number;
    budget: number;
    monthlyAverage: number;
    budgetPercentage: number;
    yearGrowth: number;
}

interface Employee {
    id: string;
    name: string;
    avatar?: string;
    initials: string;
    role: string;
    productivity: number;
    sales: number;
    ordersCompleted: number;
    rating: number;
    status: 'active' | 'busy' | 'away';
}

interface OrderStatus {
    status: string;
    count: number;
    percentage: number;
    color: string;
}

interface TeamPerformance {
    month: string;
    orders: number;
    revenue: number;
    avgTime: number;
}

@Component({
    selector: 'app-sfc-dashboard-admin',
    templateUrl: './sfc-dashboard-admin.component.html',
    styleUrls: ['./sfc-dashboard-admin.component.scss']
})
export class SfcDashboardAdminComponent implements OnInit {

    // Billing KPIs
    public billingKPI: BillingKPI = {
        yearTotal: 1250000,
        monthTotal: 125000,
        budget: 1500000,
        monthlyAverage: 104166.67,
        budgetPercentage: 83,
        yearGrowth: 15.5
    };

    // Employees Data
    public employees: Employee[] = [
        {
            id: '1',
            name: 'João Silva',
            initials: 'JS',
            role: 'Gerente de Vendas',
            productivity: 92,
            sales: 250000,
            ordersCompleted: 45,
            rating: 4.8,
            status: 'active'
        },
        {
            id: '2',
            name: 'Maria Santos',
            initials: 'MS',
            role: 'Atendente Senior',
            productivity: 88,
            sales: 180000,
            ordersCompleted: 38,
            rating: 4.6,
            status: 'active'
        },
        {
            id: '3',
            name: 'Carlos Oliveira',
            initials: 'CO',
            role: 'Atendente',
            productivity: 85,
            sales: 150000,
            ordersCompleted: 32,
            rating: 4.5,
            status: 'busy'
        },
        {
            id: '4',
            name: 'Ana Paula',
            initials: 'AP',
            role: 'Atendente',
            productivity: 90,
            sales: 175000,
            ordersCompleted: 35,
            rating: 4.7,
            status: 'active'
        },
        {
            id: '5',
            name: 'Pedro Costa',
            initials: 'PC',
            role: 'Atendente Junior',
            productivity: 78,
            sales: 120000,
            ordersCompleted: 28,
            rating: 4.3,
            status: 'away'
        }
    ];

    // Order Status Distribution
    public orderStatusData: OrderStatus[] = [
        { status: 'Concluídos', count: 156, percentage: 52, color: 'green' },
        { status: 'Em Andamento', count: 78, percentage: 26, color: 'blue' },
        { status: 'Pendentes', count: 45, percentage: 15, color: 'orange' },
        { status: 'Cancelados', count: 21, percentage: 7, color: 'red' }
    ];

    // Team Performance Over Time
    public teamPerformance: TeamPerformance[] = [
        { month: 'Jan', orders: 245, revenue: 980000, avgTime: 4.2 },
        { month: 'Fev', orders: 220, revenue: 890000, avgTime: 4.5 },
        { month: 'Mar', orders: 280, revenue: 1120000, avgTime: 3.9 },
        { month: 'Abr', orders: 265, revenue: 1060000, avgTime: 4.1 },
        { month: 'Mai', orders: 290, revenue: 1160000, avgTime: 3.8 },
        { month: 'Jun', orders: 300, revenue: 1250000, avgTime: 3.7 }
    ];

    // Charts
    public revenueChartData: any;
    public revenueChartOptions: any;
    public orderStatusChartData: any;
    public orderStatusChartOptions: any;
    public teamPerformanceChartData: any;
    public teamPerformanceChartOptions: any;
    public productivityChartData: any;
    public productivityChartOptions: any;

    public screenHeight: string = window.innerWidth > 120 ? (window.innerHeight - 530).toString() + 'px' : '400px';

    constructor(
        private router: Router,
        private translateService: TranslateService
    ) { }

    ngOnInit(): void {
        this.initializeCharts();
    }

    initializeCharts(): void {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color');
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

        // Revenue Chart
        this.revenueChartData = {
            labels: this.teamPerformance.map(item => item.month),
            datasets: [
                {
                    label: 'Faturamento (R$)',
                    data: this.teamPerformance.map(item => item.revenue),
                    fill: true,
                    borderColor: '#42A5F5',
                    backgroundColor: 'rgba(66, 165, 245, 0.2)',
                    tension: 0.4
                }
            ]
        };

        this.revenueChartOptions = {
            maintainAspectRatio: false,
            aspectRatio: 0.6,
            plugins: {
                legend: {
                    labels: { color: textColor }
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {
                        color: textColorSecondary,
                        callback: function(value: any) {
                            return 'R$ ' + (value / 1000) + 'k';
                        }
                    },
                    grid: { color: surfaceBorder, drawBorder: false }
                },
                x: {
                    ticks: { color: textColorSecondary },
                    grid: { color: surfaceBorder, drawBorder: false }
                }
            }
        };

        // Order Status Pie Chart
        this.orderStatusChartData = {
            labels: this.orderStatusData.map(item => item.status),
            datasets: [
                {
                    data: this.orderStatusData.map(item => item.count),
                    backgroundColor: [
                        '#22C55E',
                        '#3B82F6',
                        '#F97316',
                        '#EF4444'
                    ],
                    hoverBackgroundColor: [
                        '#16A34A',
                        '#2563EB',
                        '#EA580C',
                        '#DC2626'
                    ]
                }
            ]
        };

        this.orderStatusChartOptions = {
            plugins: {
                legend: {
                    labels: {
                        color: textColor,
                        usePointStyle: true
                    },
                    position: 'bottom'
                }
            }
        };

        // Team Performance Bar Chart
        this.teamPerformanceChartData = {
            labels: this.teamPerformance.map(item => item.month),
            datasets: [
                {
                    label: 'Pedidos Concluídos',
                    data: this.teamPerformance.map(item => item.orders),
                    backgroundColor: '#42A5F5',
                    borderColor: '#1E88E5',
                    borderWidth: 1
                }
            ]
        };

        this.teamPerformanceChartOptions = {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    labels: { color: textColor }
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: { color: textColorSecondary },
                    grid: { color: surfaceBorder, drawBorder: false }
                },
                x: {
                    ticks: { color: textColorSecondary },
                    grid: { color: surfaceBorder, drawBorder: false }
                }
            }
        };

        // Productivity Chart (Radar)
        this.productivityChartData = {
            labels: this.employees.map(emp => emp.initials),
            datasets: [
                {
                    label: 'Produtividade (%)',
                    data: this.employees.map(emp => emp.productivity),
                    backgroundColor: 'rgba(66, 165, 245, 0.2)',
                    borderColor: '#42A5F5',
                    pointBackgroundColor: '#42A5F5',
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: '#42A5F5'
                }
            ]
        };

        this.productivityChartOptions = {
            plugins: {
                legend: {
                    labels: { color: textColor }
                }
            },
            scales: {
                r: {
                    grid: { color: surfaceBorder },
                    pointLabels: { color: textColorSecondary },
                    ticks: {
                        color: textColorSecondary,
                        backdropColor: 'transparent'
                    }
                }
            }
        };
    }

    getStatusColor(status: string): string {
        switch (status) {
            case 'active': return 'green';
            case 'busy': return 'orange';
            case 'away': return 'gray';
            default: return 'gray';
        }
    }

    getStatusIcon(status: string): string {
        switch (status) {
            case 'active': return 'pi-check-circle';
            case 'busy': return 'pi-clock';
            case 'away': return 'pi-minus-circle';
            default: return 'pi-circle';
        }
    }

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    }

    navigateToReports(): void {
        this.router.navigate(['/reports']);
    }

    navigateToEmployees(): void {
        this.router.navigate(['/employees']);
    }

    navigateToOrders(): void {
        this.router.navigate(['/orders']);
    }
}

